import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface PublishConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onSaveAsDraft?: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  isNewCampaign?: boolean;
  campaignName?: string;
  isCampaignPaused?: boolean;
  /**
   * The ad's promotion when it is still a DRAFT. Confirming then publishes
   * it with the ad, so the dialog names it first: an owner must never publish
   * a promotion they weren't shown.
   */
  draftPromotionName?: string | null;
}

/**
 * Publish confirmation modal for ad wizard
 */
export function PublishConfirmModal({
  open,
  onOpenChange,
  onConfirm,
  onSaveAsDraft,
  onCancel,
  isLoading,
  isNewCampaign,
  campaignName,
  isCampaignPaused,
  draftPromotionName,
}: PublishConfirmModalProps) {
  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {draftPromotionName ? 'Publish ad and promotion' : 'Publish Ad'}
          </DialogTitle>
          <DialogDescription>
            {isNewCampaign && campaignName ? (
              <>
                You are about to create a new campaign &quot;{campaignName}
                &quot; and publish your ad. This will make your ad live on Meta.
              </>
            ) : isCampaignPaused ? (
              <>
                The campaign{campaignName ? ` "${campaignName}"` : ''} is
                currently paused. Publishing this ad will also activate the
                campaign, making it live on Meta.
              </>
            ) : (
              <>
                Are you sure you want to publish this ad? This will make your ad
                live on Meta.
              </>
            )}
          </DialogDescription>
          {draftPromotionName ? (
            <p
              className="rounded-md border border-amber-200/70 bg-amber-50/60 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
              data-testid="publish-draft-promotion-notice"
            >
              This ad advertises the promotion &ldquo;{draftPromotionName}
              &rdquo;, which is still a draft. Publishing will also publish the
              promotion &ldquo;{draftPromotionName}&rdquo; so customers can
              claim it.
            </p>
          ) : null}
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          {onSaveAsDraft && (
            <Button
              variant="secondary"
              onClick={onSaveAsDraft}
              disabled={isLoading}
            >
              Save as Draft
            </Button>
          )}
          <Button onClick={onConfirm} disabled={isLoading}>
            {isLoading
              ? 'Publishing...'
              : draftPromotionName
                ? 'Publish ad and promotion'
                : 'Publish'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
